import * as React from "react";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { visuallyHidden } from "@mui/utils";
import { jsPDF } from "jspdf"; //or use your library of choice here
import autoTable from "jspdf-autotable";
import { Download } from "@mui/icons-material";
import "../../../App.css";
import { hzIncidents } from "../../hazardIncidentsLayers/IncidentsMenuDropDown";
import SummeryHz from "../SummeryHz";
import SumMainHz from "../SumMainHz";

interface Data {
  w_name: string;
  w_status: string;
  adm3_en: string;
  issued_time: string;
  w_desc: string;
  adm4_en: string;
  w_id: string;
  bwater_level: string;
  end_time: string;
  hwater_level: string;
  img_url: string;
  imp_communication: string;
  imp_electricity: string;
  imp_house: string;
  imp_human: string;
  imp_response: string;
  imp_roadblock: string;
  imp_roaddamage: string;
  imp_water: string;
  impdes_communication: string;
  impdes_electricity: string;
  impdes_house: string;
  impdes_human: string;
  impdes_roadblock: string;
  impdes_roaddamage: string;
  impdes_water: string;
  occurance_date: string;
  observed_date: string;
  tp_number: string;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
  [x: string]: any;
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: HeadCell[] = [
  {
    id: "w_name",
    numeric: false,
    disablePadding: false,
    label: "Incident Name",
  },
  {
    id: "w_status",
    numeric: false,
    disablePadding: false,
    label: "Incident Status",
  },
  {
    id: "adm3_en",
    numeric: false,
    disablePadding: false,
    label: "Division",
  },
  {
    id: "issued_time",
    numeric: false,
    disablePadding: false,
    label: "Reported Time",
  },
];

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, numSelected, rowCount, onRequestSort } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
              style={{ color: "var(--text-color)" }}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface EnhancedTableToolbarProps {
  numSelected: number;
  rows: any;
}

function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
  const handleExportRows = () => {
    const doc = new jsPDF();
    const tableData = props.rows.map((row: Data) => Object.values(row));
    const tableHeaders = headCells.map((c) => c.label);

    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
    });

    doc.save("mrt-pdf-example.pdf");
  };
  const { numSelected } = props;

  return (
    <Toolbar
      sx={{
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      {
        <Typography
          sx={{ flex: "1 1 100%", color: "var(--text-color)" }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Reported Incidents
        </Typography>
      }
      {
        <Tooltip title="Filter list">
          <IconButton onClick={() => handleExportRows()}  onTouchStart={() => handleExportRows()}>
            <Download />
          </IconButton>
        </Tooltip>
      }
    </Toolbar>
  );
}

interface HzTypeDataTableProps {
  hzDatafromSummery: hzIncidents[]; // Adjust the type as per your data structure
}

export default function HzTypeDataTable({
  hzDatafromSummery,
}: HzTypeDataTableProps) {
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("adm3_en");
  const [selected, setSelected] = React.useState<readonly number[]>([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [rows, setRows] = React.useState<Array<any>>([]);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [expandedRowIndices, setExpandedRowIndices] = React.useState<number[]>(
    []
  );
  const [searchQuery, setSearchQuery] = React.useState<string>(""); 


  const handleRowClick = (tableIndex: number, rowIndex: number) => {
    setExpandedRowIndices((prev) => {
      // Create a copy of the current expanded state
      const newExpandedRowIndices = [...prev];
      const combinedIndex = tableIndex * 100 + rowIndex; // unique index for each table-row combo
      if (newExpandedRowIndices.includes(combinedIndex)) {
        // If already expanded, collapse it
        return newExpandedRowIndices.filter((index) => index !== combinedIndex);
      } else {
        // Expand it
        return [...newExpandedRowIndices, combinedIndex];
      }
    });
  };

  React.useEffect(() => {
    const result = hzDatafromSummery;
    if (result.length > 0) {
      const formattedRows = result.map((warning: Data) => ({
        w_name: warning.w_name,
        w_status: warning.w_status,
        adm3_en: warning.adm3_en,
        issued_time: warning.issued_time,
        w_desc: warning.w_desc,
        adm4_en: warning.adm4_en,
        w_id: warning.w_id,
        bwater_level: warning.bwater_level,
        end_time: warning.end_time,
        hwater_level: warning.hwater_level,
        img_url: warning.img_url,
        imp_communication: warning.imp_communication,
        imp_electricity: warning.imp_electricity,
        imp_house: warning.imp_house,
        imp_human: warning.imp_human,
        imp_response: warning.imp_response,
        imp_roadblock: warning.imp_roadblock,
        imp_roaddamage: warning.imp_roaddamage,
        imp_water: warning.imp_water,
        impdes_communication: warning.impdes_communication,
        impdes_electricity: warning.impdes_electricity,
        impdes_house: warning.impdes_house,
        impdes_human: warning.impdes_human,
        impdes_roadblock: warning.impdes_roadblock,
        impdes_roaddamage: warning.impdes_roaddamage,
        impdes_water: warning.impdes_water,
        occurance_date: warning.occurance_date,
        observed_date: warning.observed_date,
        tp_number: warning.tp_number,
      }));
      setRows((prevTables) => {
        // Flatten previous tables and extract severities
        const existingSeverities = new Set(
          prevTables.flat().map((row) => row.adm3_en)
        );

        // Filter out rows with duplicate severities
        const uniqueFormattedRows = formattedRows.filter(
          (row) => !existingSeverities.has(row.adm3_en)
        );

        // Only add unique rows
        return uniqueFormattedRows.length > 0
          ? [...prevTables, uniqueFormattedRows]
          : prevTables;
      });
    }
  }, [hzDatafromSummery]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to the first page on search
  };

  const filteredRows = rows.filter((row: Data) => {
    const searchFields = `${row.w_name} ${row.w_status} ${row.adm3_en} ${row.issued_time}`;
    return searchFields.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(filteredRows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, filteredRows]
  );

  return (
    <Box
      sx={{
        width: "90%",
        /* border: "1px solid red" */ display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        zIndex: "1000",
      }}
    >
      {rows.map((tableRows, tableIndex) => (
        <Paper
          key={tableIndex}
          sx={{
            width: "100%",
            //mb: 2,
            backgroundColor: "rgba(255, 255, 255, 0)",
          }}
        >
       <Box sx={{ padding: "16px", display: "flex", justifyContent: "flex-end" }}>
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearchChange}
            style={{
              padding: "8px",
              width: "40%",
              borderRadius: "4px",
            }}
          />
        </Box>
          <EnhancedTableToolbar
            numSelected={selected.length}
            rows={tableRows}
          />
          <TableContainer>
            <Table
              aria-labelledby="tableTitle"
              size={dense ? "small" : "medium"}
            >
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={tableRows.length}
              />
              <TableBody>
                {tableRows.map((row: Data, index: number) => {
                  const isExpanded = expandedRowIndices.includes(
                    tableIndex * 100 + index
                  );

                  return (
                    <React.Fragment key={index}>
                      <TableRow
                        hover
                        onClick={() => handleRowClick(tableIndex, index)}
                        onTouchStart={() => handleRowClick(tableIndex, index)}
                        tabIndex={-1}
                        sx={{
                          cursor: "pointer",
                          color: "var(--text-color)",
                          fontSize: "14px",
                        }}
                      >
                        <TableCell
                          align="left"
                          style={{ color: "var(--text-color)" }}
                        >
                          {row.w_name}
                        </TableCell>
                        <TableCell
                          align="left"
                          style={{ color: "var(--text-color)" }}
                        >
                          {row.w_status}
                        </TableCell>
                        <TableCell
                          align="left"
                          style={{ color: "var(--text-color)" }}
                        >
                          {row.adm3_en}
                        </TableCell>
                        <TableCell
                          align="left"
                          style={{ color: "var(--text-color)" }}
                        >
                          {row.issued_time}
                        </TableCell>
                      </TableRow>

                      {isExpanded && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            style={{ paddingLeft: "16px" }}
                          >
                            <SumMainHz
                              w_id={row.w_id}
                              adm4_en={row.adm4_en}
                              bwater_level={row.bwater_level}
                              end_time={row.end_time}
                              hwater_level={row.hwater_level}
                              img_url={row.img_url}
                              imp_communication={row.imp_communication}
                              imp_electricity={row.imp_electricity}
                              imp_house={row.imp_house}
                              imp_human={row.imp_human}
                              imp_response={row.imp_response}
                              imp_roadblock={row.imp_roadblock}
                              imp_roaddamage={row.imp_roaddamage}
                              imp_water={row.imp_water}
                              impdes_communication={row.impdes_communication}
                              impdes_electricity={row.impdes_electricity}
                              impdes_house={row.impdes_house}
                              impdes_human={row.impdes_human}
                              impdes_roadblock={row.impdes_roadblock}
                              impdes_roaddamage={row.impdes_roaddamage}
                              impdes_water={row.impdes_water}
                              issued_time={row.issued_time}
                              occurance_date={row.occurance_date}
                              observed_date={row.observed_date}
                              tp_number={row.tp_number}
                              w_desc={row.w_desc}
                              w_name={row.w_name}
                              w_status={row.w_status}
                              adm3_en={row.adm3_en}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            style={{ color: "var(--text-color)" }}
          />
        </Paper>
      ))}
      <FormControlLabel
        control={<Switch checked={dense} onChange={handleChangeDense} />}
        label="Dense padding"
        style={{ color: "var(--text-color)" }}
      />
    </Box>
  );
}
